import { useState, useCallback, useRef, useMemo } from 'react';
import { useCurrentUser } from 'FrontRoyalAngular';
import { getCohort } from 'Users';
import { type CamelCasedStudentNetworkEvent } from 'StudentNetworkEvent';
import { useGetStudentNetworkEventsQuery } from '../api/studentNetworkEventsV2Api';
import { type StudentNetworkEventFilters } from '../api/studentNetworkV2Api.types';

type EventTab = 'featured' | 'upcoming' | 'past';

const PAGE_SIZE = 20;

type UseNetworkEventsOptions = {
    tab?: EventTab;
    keywordSearch?: string;
    eventTypes?: string[];
};

// oxlint-disable-next-line max-lines-per-function -- paginated hook with filter/tab reset logic
export function useNetworkEvents({ tab = 'featured', keywordSearch = '', eventTypes }: UseNetworkEventsOptions = {}) {
    const currentUser = useCurrentUser();
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const cohortIds = useMemo(() => (cohort ? [cohort.id] : []), [cohort]);

    // Stabilize end_time so it doesn't change on every re-render while staying on the past tab.
    // A new timestamp is captured only when the user switches to the past tab.
    const pastEndTimeRef = useRef(0);
    const prevTabRef = useRef(tab);
    if (tab === 'past' && (prevTabRef.current !== 'past' || pastEndTimeRef.current === 0)) {
        pastEndTimeRef.current = Math.floor(Date.now() / 1000);
    }
    prevTabRef.current = tab;

    const filters: StudentNetworkEventFilters = useMemo(() => {
        const base: StudentNetworkEventFilters = {
            cohort_ids: cohortIds,
            include_tbd: true,
        };

        if (tab === 'featured') base.featured = true;
        if (tab === 'past') {
            base.end_time = pastEndTimeRef.current;
        }

        const trimmed = keywordSearch.trim();
        if (trimmed) base.keyword_search = trimmed;

        if (eventTypes && eventTypes.length > 0) base.event_type = eventTypes;

        return base;
    }, [cohortIds, tab, keywordSearch, eventTypes]);

    const direction = tab === 'past' ? 'DESC' : 'ASC';

    const [offset, setOffset] = useState(0);
    const filtersKey = `${JSON.stringify(filters)}-${tab}`;
    const [prevFiltersKey, setPrevFiltersKey] = useState(filtersKey);
    const accumulatedRef = useRef<CamelCasedStudentNetworkEvent[]>([]);

    // React "adjust state during render" pattern — calling setState synchronously
    // during render is intentional and officially supported by React for deriving
    // state from props/deps without an extra commit cycle. See React docs on
    // "adjusting state based on props". This avoids one render with stale data.
    if (filtersKey !== prevFiltersKey) {
        setPrevFiltersKey(filtersKey);
        accumulatedRef.current = [];
        setOffset(0);
    }

    const { currentData, isFetching, isLoading, isError, error, refetch } = useGetStudentNetworkEventsQuery({
        filters,
        limit: PAGE_SIZE,
        offset,
        sort: 'start_time',
        direction,
    });

    const events = useMemo(() => {
        if (!currentData?.events) return accumulatedRef.current;

        if (offset === 0) {
            accumulatedRef.current = currentData.events;
        } else {
            const existingIds = new Set(accumulatedRef.current.map(e => e.id));
            const newEvents = currentData.events.filter(e => !existingIds.has(e.id));
            if (newEvents.length > 0) {
                accumulatedRef.current = [...accumulatedRef.current, ...newEvents];
            }
        }

        return accumulatedRef.current;
    }, [currentData, offset]);

    // Persist the last-known totalCount across filter/tab changes so the UI
    // doesn't flash "0 results" while the new query loads.
    const totalCountRef = useRef(0);
    if (currentData?.totalCount != null) totalCountRef.current = currentData.totalCount;
    const totalCount = totalCountRef.current;

    const loadMore = useCallback(() => {
        if (!isFetching && currentData && offset + PAGE_SIZE < currentData.totalCount) {
            setOffset(prev => prev + PAGE_SIZE);
        }
    }, [currentData, offset, isFetching]);

    const hasMore = events.length < totalCount;

    // Show spinner when fetching the first page (initial load or tab/filter change)
    const isRefreshing = isFetching && offset === 0;

    return {
        events,
        totalCount,
        isLoading: isLoading || isRefreshing,
        isFetching,
        isError,
        error,
        loadMore,
        hasMore,
        retry: refetch,
    };
}
